import { Calendar, MapPin, Bookmark, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { mockOpportunities } from '@/data/mockData';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const typeLabels = {
  emploi: 'Emploi',
  stage: 'Stage',
  bourse: 'Bourse',
  formation: 'Formation',
  volontariat: 'Volontariat'
};

const typeColors = {
  emploi: 'bg-primary',
  stage: 'bg-secondary',
  bourse: 'bg-success',
  formation: 'bg-accent',
  volontariat: 'bg-destructive'
};

export function FeaturedOpportunities() {
  const featuredOpportunities = mockOpportunities.filter(opp => opp.featured).slice(0, 6);

  return (
    <section className="py-16 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-poppins font-bold text-foreground mb-4">
            Opportunités à la Une
          </h2>
          <p className="text-lg text-muted-foreground font-inter max-w-2xl mx-auto">
            Les dernières opportunités sélectionnées spécialement pour vous
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featuredOpportunities.map((opportunity, index) => (
            <Card key={opportunity.id} className="group bg-card hover-lift border border-border hover:border-primary transition-all overflow-hidden relative">
              {/* Simple Featured Tag */}
              <div className="absolute top-4 right-4 z-10">
                <div className="bg-primary text-primary-foreground px-3 py-1 rounded-md text-xs font-semibold">
                  À LA UNE
                </div>
              </div>
              
              {/* Type Badge */}
              <div className="p-1">
                <div className={`${typeColors[opportunity.type]} h-1 rounded-t-lg`}></div>
              </div>
              
              <div className="p-6">
                {/* Company Logo & Info */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center text-white font-bold">
                    {opportunity.company.substring(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground uppercase">{opportunity.company}</p>
                    <h3 className="font-bold text-lg text-foreground line-clamp-1 group-hover:text-primary transition-colors">
                      {opportunity.title}
                    </h3>
                  </div>
                  <button className="p-2 hover:bg-muted rounded-full transition-colors">
                    <Bookmark className="h-5 w-5 text-muted-foreground hover:text-primary" />
                  </button>
                </div>
                
                {/* Description */}
                <p className="text-sm text-muted-foreground font-inter mb-4 line-clamp-2">
                  {opportunity.description}
                </p>
                
                {/* Meta Info */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MapPin className="h-4 w-4" />
                    <span className="font-inter">{opportunity.arrondissement || opportunity.location}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    <span className="font-inter">
                      Publié le {format(opportunity.publishedAt, 'dd MMMM', { locale: fr })}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="h-4 w-4 text-destructive" />
                    <span className="text-destructive font-semibold font-inter">
                      Deadline: {format(opportunity.deadline, 'dd MMMM yyyy', { locale: fr })}
                    </span>
                  </div>
                </div>
                
                {/* Salary if available */}
                {opportunity.salary && (
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-success font-inter">{opportunity.salary}</p>
                  </div>
                )}
                
                {/* Action Buttons */}
                <div className="flex gap-2">
                  <Link to={`/opportunity/${opportunity.id}`} className="flex-1">
                    <Button variant="default" size="sm" className="w-full">
                      Voir détails
                    </Button>
                  </Link>
                  <Button variant="secondary" size="sm">
                    Postuler
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
        
        <div className="text-center mt-10">
          <Link to="/opportunities">
            <Button variant="outline" size="lg">
              Voir toutes les opportunités
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}